import React, { useEffect, useState } from 'react';
import {
  DataGrid
} from '@mui/x-data-grid';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  FormControl,
  DialogActions,
  Select,
  MenuItem,
  Grid,
  CardContent,
  Card,
  TextField,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import { vendorApi } from '../../api/api';
import { investorApi } from '../../api/api';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';

const ApproveMarketUpdateGrid = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState('');
  const [legalTeam, setLegalTeam] = useState([]);

  const handleStatusFilter = (e) => setStatusFilter(e.target.value);

  const fetchPendingUpdates = async () => {
    try {
      setLoading(true);
      const response = await vendorApi.getAllPendingMarketPrice();
      const formattedData = response.data.map((item) => ({
        id: item.id,
        propertyName: item.propertyName,
        vendorName: item.vendorName,
        address: item.address,
        city: item.city,
        sqftPrice: item.sqftPrice,
        status: item.status,
        Previousprice: item.oldSqftPrice,
        projectId: item.ProjectId,
      }));
      setRows(formattedData);
    } catch (error) {
      console.error('Error fetching pending market updates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique cities for dropdown
  const uniqueCities = [...new Set(rows.map(row => row.city).filter(Boolean))];

  const filteredData = rows.filter((row) => {
    const matchesStatus = statusFilter === 'All' || row.status === statusFilter;
    const matchesSearch =
      row.propertyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.vendorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.city?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const cityMatch = cityFilter === '' || row.city === cityFilter;

    return matchesStatus && matchesSearch && cityMatch;
  });

  const assignLegalTeam = async () => {
    if (!selectedProperty || !selectedTeamMember) return;

    try {
      await vendorApi.addLegalTeamMarketUpdate({
        propertyId: selectedProperty.id,
        userId: selectedTeamMember,
        SqFtPrice: selectedProperty.sqftPrice
      });
      alert('Assigned successfully!');
      closeAssignModal();
    } catch (error) {
      console.error('Error assigning legal team:', error);
      alert('Assignment failed.');
    }
  };

  const handleApprove = (row) => {
    navigate('/marketupdate/approve', { state: { marketData: row } });
  };

  const openAssignModal = (row) => {
    setSelectedProperty(row);
    setSelectedTeamMember('');
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedProperty(null);
  };

  useEffect(() => {
    const getLegalTeam = async () => {
      try {
        const response = await investorApi.getLegalTeam();
        setLegalTeam(response.data);
      } catch (error) {
        console.error('Error fetching legal team:', error);
      }
    };
    getLegalTeam();
    fetchPendingUpdates();
  }, []);

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'propertyName', headerName: 'Property Name', width: 180 },
    { field: 'vendorName', headerName: 'Vendor Name', width: 150 },
    { field: 'address', headerName: 'Address', width: 200 },
    { field: 'city', headerName: 'City', width: 120 },
    { field: 'sqftPrice', headerName: 'SqFt Price', width: 130, type: 'number' },
    {
      field: 'Previousprice',
      headerName: 'Previous Sqft price',
      width: 200,
      type: 'number',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        let color = 'gray';
        if (params.value === 'Pending') color = 'orange';
        else if (params.value === 'Approved') color = 'green';
        else if (params.value === 'Rejected') color = 'red';
        else if (params.value === 'InProgress') color = '#1976d2';
        return <span style={{ color, fontWeight: 600 }}>{params.value}</span>;
      },
    },
    {
      field: 'approve',
      headerName: 'Action',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleApprove(params.row)}
            disabled={params.row.status === 'Approved' || params.row.status === 'InProgress'}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => openAssignModal(params.row)}
          >
            Assign
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Approve Market Updates
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div >
          {/* Single Row Filter Card */}
          <Card sx={{ border: '1px solid lightgray', borderRadius: '8px', mb: 2 }}>
            <CardContent sx={{ p: 2 }}>

              {/* Main Filter Row - All in one line */}
              <Grid container spacing={2} alignItems="center">
                {/* Search - 4 columns */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: '#666', fontSize: 20 }} />,
                    }}
                  />
                </Grid>

                {/* Status - 3 columns */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={handleStatusFilter}
                      label="Status"
                    >
                      <MenuItem value="All">All Status</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="InProgress">InProgress</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* City - 3 columns */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>City</InputLabel>
                    <Select
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      label="City"
                    >
                      <MenuItem value="">All Cities</MenuItem>
                      {uniqueCities.map((city) => (
                        <MenuItem key={city} value={city}>{city}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Clear Button - 2 columns */}
                {/* <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('All');
                      setCityFilter('');
                    }}
                    sx={{ 
                      color: '#f44336',
                      borderColor: '#f44336',
                      '&:hover': {
                        backgroundColor: '#ffebee',
                        borderColor: '#f44336',
                      }
                    }}
                  >
                    Clear Filters
                  </Button>
                </Grid> */}
              </Grid>
            </CardContent>
          </Card>

          {/* Data Grid Card */}
          <Card sx={{ height: '85vh', width: '100%', overflowX: 'hidden' }}>
            <DataGrid
              rows={filteredData}
              columns={columns}
              pageSize={7}
              rowsPerPageOptions={[7]}
              disableSelectionOnClick
              autoHeight={false}
               sx={{
              height: 650,
              border: '1px solid #ccc',
              borderRadius: 2,
              fontSize: '0.9rem',
              backgroundColor: '#ffffff',
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f0f0f0ff',
              },
              '& .MuiDataGrid-cell': {
                color: '#333',
                borderBottom: '1px solid #ddd',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f0f0f0ff',
                color: 'black',
                fontWeight: 'bold',
              },
              '& .MuiTablePagination-root': {
                fontSize: '0.875rem',
              },
            }}
            />
          </Card>
        </div>
      )}

      <Dialog open={assignModalOpen} onClose={closeAssignModal} fullWidth maxWidth="sm">
        <DialogTitle>
          Assign{' '}
          <span style={{ color: '#1976d2' }}>{selectedProperty?.propertyName}</span> for Market Update
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Legal Team Member</InputLabel>
            <Select
              value={selectedTeamMember}
              onChange={(e) => setSelectedTeamMember(e.target.value)}
              label="Legal Team Member"
            >
              {Array.isArray(legalTeam) &&
                legalTeam.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAssignModal}>Cancel</Button>
          <Button variant="contained" onClick={assignLegalTeam} disabled={!selectedTeamMember}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApproveMarketUpdateGrid;